<script setup>
/**
 * AppFrame — a premium "app window" frame for showing REAL app components as
 * non-interactive screenshots on marketing pages. Slotted content is the live
 * component tree (fed demo props) so the imagery is always pixel-true.
 *
 * Design: window chrome bar (traffic-light dots + caption), soft drop shadow,
 * a slight tilt that straightens on hover, and a coloured glow behind the
 * frame to suggest energy/movement. Content is inert (pointer-events/select
 * disabled, hidden from assistive tech).
 */
defineProps({
  caption: {type: String, default: 'Live preview'},
  // glow colour: brand | ladder | tournament | ink
  tone: {type: String, default: 'brand'},
  // resting tilt in degrees (set 0 to sit flat)
  tilt: {type: Number, default: -3},
  // show the coloured glow behind the frame
  glow: {type: Boolean, default: true},
});
</script>

<template>
  <div class="af" :class="`af--${tone}`" :style="{ '--af-tilt': `${tilt}deg` }">
    <div v-if="glow" class="af-glow" aria-hidden="true"/>
    <figure class="af-window">
      <figcaption class="af-bar">
        <span class="af-dots"><i/><i/><i/></span>
        <span class="af-cap">{{ caption }}</span>
      </figcaption>
      <div class="af-body" aria-hidden="true" inert>
        <slot/>
      </div>
    </figure>
  </div>
</template>

<style scoped lang="scss">
.af {
  position: relative;
  --af-glow-1: rgba(211, 41, 70, 0.50);
  --af-glow-2: rgba(222, 135, 10, 0.30);

  &--ladder {
    --af-glow-1: rgba(36, 119, 200, 0.50);
    --af-glow-2: rgba(30, 158, 118, 0.28);
  }

  &--tournament {
    --af-glow-1: rgba(222, 135, 10, 0.52);
    --af-glow-2: rgba(211, 41, 70, 0.28);
  }

  &--ink {
    --af-glow-1: rgba(26, 26, 26, 0.22);
    --af-glow-2: rgba(26, 26, 26, 0.10);
  }
}

.af-glow {
  position: absolute;
  inset: -16% -12%;
  z-index: 0;
  background:
    radial-gradient(42% 55% at 22% 26%, var(--af-glow-1), transparent 70%),
    radial-gradient(46% 60% at 82% 80%, var(--af-glow-2), transparent 72%);
  filter: blur(30px);
  opacity: 0.9;
  pointer-events: none;
}

.af-window {
  position: relative;
  z-index: 1;
  margin: 0;
  background: rgb(var(--v-theme-surface));
  /* reset inherited text colour — the frame may sit on a coloured band
     (e.g. the hero sets white text), but its content is a real app surface */
  color: rgb(var(--v-theme-text));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 16px;
  overflow: hidden;
  box-shadow:
    0 28px 56px -24px rgba(26, 26, 26, 0.45),
    0 10px 22px -16px rgba(26, 26, 26, 0.30);
  transform: rotate(var(--af-tilt));
  transition: transform 0.4s cubic-bezier(0.2, 0.7, 0.2, 1), box-shadow 0.4s ease;
}

.af:hover .af-window {
  transform: rotate(0deg) translateY(-3px);
  box-shadow:
    0 36px 66px -22px rgba(26, 26, 26, 0.50),
    0 12px 26px -16px rgba(26, 26, 26, 0.32);
}

.af-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 13px;
  background: rgb(var(--v-theme-light));
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
}

.af-dots {
  display: inline-flex;
  gap: 5px;
  flex-shrink: 0;

  i {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: rgb(var(--v-theme-border));

    &:first-child { background: #FF5F57; }
    &:nth-child(2) { background: #FEBC2E; }
    &:nth-child(3) { background: #28C840; }
  }
}

.af-cap {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  color: rgb(var(--v-theme-charcoal));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.af-body {
  pointer-events: none;
  user-select: none;
  padding: 14px;
  overflow-x: auto;
  background: rgb(var(--v-theme-surface));
}

/* no tilt on small screens — keeps the frame from overflowing */
@media (max-width: 600px) {
  .af-window {
    transform: rotate(0deg);
  }
}
</style>
